import useToasts from 'hooks/useToasts';
import isBoolean from 'lodash/isBoolean';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
    getMagicLinkInfo,
    loggedInWithToken,
    updateExhibitor,
} from 'store/actions/auth';
import { verifyExhibitorToken } from 'store/actions/Lounge';
import apiRequest from 'utils/api';
import {
    EXHIBITOR_CODE,
    CODE,
    MAGIC_LINK_DETAILS,
} from 'utils/constants/airmeet';
import { logger } from 'utils/logger';

const INVITE_AUTH_TOKEN_API_URL = 'airmeet/session/invite/auth';
const MAGIC_LINK_ERRORS = {
    REGISTRATION_LIMIT_EXCEEDED: 'REGISTRATION_LIMIT_EXCEEDED',
    EVENT_FINISHED: 'EVENT_FINISHED',
};
const isValidAccessCode = (eventDetails, airmeetId) => {
    return eventDetails?.airmeetId === airmeetId;
};
export const accessCodeInUrl = (queryParam, history) => {
    const query = new URLSearchParams(history.location.search);
    return query.get(queryParam);
};
export const getQueryParamFromUrl = (history) => {
    const query = new URLSearchParams(history.location.search);
    if (query.get(CODE)) {
        return CODE;
    } else if (query.get(EXHIBITOR_CODE)) {
        return EXHIBITOR_CODE;
    }
    return '';
};
export const getQueryParamFromSession = (airmeetId) => {
    let accessCodeDetail = getAccessCode(CODE, airmeetId, {
        eventDetail: true,
    });
    if (accessCodeDetail?.[CODE]) {
        return CODE;
    } else if (accessCodeDetail?.[EXHIBITOR_CODE]) {
        return EXHIBITOR_CODE;
    }
    return '';
};
export const getAccessCodeFromSession = () => {
    let eventDetail = sessionStorage.getItem(MAGIC_LINK_DETAILS);
    if (!eventDetail) {
        return {};
    }
    return JSON.parse(eventDetail);
};
export const setAccessCodeInSession = ({
    queryParam,
    tokenVal,
    airmeetId,
    replaceUrl = '',
    isSpeaker = false,
}) => {
    let eventDetail = {
        [queryParam]: tokenVal,
        airmeetId: airmeetId,
    };
    if (isSpeaker) {
        eventDetail['isSpeaker'] = true;
    }
    sessionStorage.setItem(MAGIC_LINK_DETAILS, JSON.stringify(eventDetail));
    if (replaceUrl) {
        window.location.href = replaceUrl;
    }
};
export const authenticateAccessCode = (airmeetId) => {
    let eventDetail = getAccessCodeFromSession();
    if (eventDetail && !isValidAccessCode(eventDetail, airmeetId)) {
        logger.info('access code authentication failed', {
            eventDetails: eventDetail,
            airmeetId,
        });
        sessionStorage.removeItem(MAGIC_LINK_DETAILS);
    }
};
export const getAccessCode = (queryName = '', airmeetId, options = {}) => {
    let eventDetails = getAccessCodeFromSession();
    if (!eventDetails || !isValidAccessCode(eventDetails, airmeetId)) {
        return '';
    }
    return options?.eventDetail ? eventDetails : eventDetails[queryName];
};
const useAccessCodeUserToken = (onSucess, onError, props = {}) => {
    const {
        queryName = CODE,
        airmeetId = null,
        refreshDirectUser = false,
        isMobileWebView,
        liveAirmeet = false,
    } = props;
    const dispatch = useDispatch();
    const { errorToast } = useToasts();
    const history = useHistory();
    const [accessCodeUserData, setAccessCodeUserData] = useState(null);
    const redirectAndReloadToEventLandingPage = () => {
        setTimeout(() => {
            sessionStorage.removeItem(MAGIC_LINK_DETAILS);
            window.location.href = `/e/${airmeetId}?preview=true`;
        }, 3000);
    };

    const ticketsConfigured = useSelector(
        (state) => state?.lounge?.airmeet?.ticketsInfo?.ticketsConfigured
    );
    const submit = async (token) => {
        try {
            if (queryName === 't') {
                const response = await apiRequest(
                    'POST',
                    INVITE_AUTH_TOKEN_API_URL,
                    {
                        token,
                    }
                );

                // No error thrown means success
                onSucess(response);
                setAccessCodeUserData({
                    user: response.user,
                    authToken: response.authToken,
                    accessCode: token,
                    airmeetId: response.session.airmeet_id,
                });
            } else if (queryName === EXHIBITOR_CODE) {
                const response = await dispatch(
                    verifyExhibitorToken(airmeetId, token)
                );
                if (response.error) {
                    return;
                }
                dispatch(
                    updateExhibitor({
                        validToken: response.is_token_valid,
                        token: token,
                    })
                );
            } else if (airmeetId) {
                dispatch(getMagicLinkInfo({ airmeetId, code: token })).then(
                    (response) => {
                        if (response.error) {
                            const {
                                REGISTRATION_LIMIT_EXCEEDED,
                                EVENT_FINISHED,
                            } = MAGIC_LINK_ERRORS;

                            if (
                                [
                                    REGISTRATION_LIMIT_EXCEEDED,
                                    EVENT_FINISHED,
                                ].includes(response.payload?.json?.code)
                            ) {
                                errorToast(response.payload?.json?.message);
                                redirectAndReloadToEventLandingPage();
                            } else {
                                errorToast(
                                    ticketsConfigured
                                        ? 'Oops! Either the event access link is incorrect OR this has been converted to a ticketed event!! Please try accessing the event via the correct link OR purchase a ticket to register for the event. Reach out to the event organizer for any further assistance.'
                                        : 'Looks like you copied the event link wrong. Please check the URL or try again'
                                );
                            }
                        } else {
                            // No error thrown means success
                            const {
                                auth_token: authToken,
                                ...user
                            } = response.payload;
                            const data = {
                                user: {
                                    ...user,
                                    ...(user.params || {}),
                                    guest: user.is_guest,
                                    direct: user.is_guest,
                                },
                                authToken,
                                accessCode: token,
                                airmeetId,
                            };
                            onSucess(data);
                            setAccessCodeUserData(data);
                        }
                    }
                );
            }
        } catch (error) {
            if (error.code === 'NOT_FOUND' && error.response.status === 404) {
                onError();
                return;
            }
            errorToast('Could not verify. Please try after a while.');
        }
    };

    const accessCode = useMemo(() => {
        if (queryName === 't' || isMobileWebView) {
            const query = new URLSearchParams(history.location.search);
            return query.get(queryName);
        }
        return getAccessCode(queryName, airmeetId);
    }, [queryName, airmeetId, history, isMobileWebView]);

    const isTicketingEnabled = useCallback(() => {
        if (
            getQueryParamFromSession(airmeetId) === CODE &&
            !getAccessCodeFromSession().isSpeaker &&
            !liveAirmeet
        ) {
            if (isBoolean(ticketsConfigured)) {
                return true;
            }
            return false;
        }
        return true;
    }, [ticketsConfigured]);

    useEffect(() => {
        // Next, try to log the speaker in
        accessCode && isTicketingEnabled() && submit(accessCode);
    }, [accessCode, refreshDirectUser, isTicketingEnabled]);

    const loginWithAccessCodeUser = useCallback(async () => {
        const { authToken, accessCode, user } = accessCodeUserData;
        dispatch(
            loggedInWithToken({
                token: authToken,
                accessCode,
                user: { guest: false, ...user },
            })
        );
    });

    return {
        accessCodeUser: accessCodeUserData ? accessCodeUserData.user : null,
        airmeetId: accessCodeUserData ? accessCodeUserData.airmeetId : null,
        accessCode: queryName !== EXHIBITOR_CODE ? accessCode : '',
        exhibitorToken: queryName === EXHIBITOR_CODE ? accessCode : '',
        loginWithAccessCodeUser,
    };
};

export default useAccessCodeUserToken;
